import { runWithLoading } from '../../redux/loadingOperation'
import firebase from 'firebase/compat/app'

import {
    addContactFeedsChain,
    callEnrichContactProfile,
    deleteFolderFilesInStorage,
    getDb,
    getId,
    getObjectFollowersIds,
    globalWatcherUnsub,
    logEvent,
    mapContactData,
    proccessPictureForAvatar,
    tryAddFollower,
    updateContactPhotoFeedsChain,
    uploadAvatarPhotos,
} from '../firestore'
import {
    createContactAssistantChangedFeed,
    createContactCompanyChangedFeed,
    createContactDeletedFeed,
    createContactDescriptionChangedFeed,
    createContactEmailChangedFeed,
    createContactHighlightChangedFeed,
    createContactNameChangedFeed,
    createContactPhoneNumberChangedFeed,
    createContactPrivacyChangedFeed,
    createContactRoleChangedFeed,
} from './contactUpdates'
import store from '../../../redux/store'
import { BatchWrapper } from '../../../functions/BatchWrapper/batchWrapper'
import { FOLLOWER_CONTACTS_TYPE } from '../../../components/Followers/FollowerConstants'
import TasksHelper from '../../../components/TaskListView/Utils/TasksHelper'
import {
    setSelectedNavItem,
    setSelectedSidebarTab,
    setSelectedTypeOfProject,
    switchProject,
} from '../../../redux/actions'
import NavigationService from '../../../utils/NavigationService'
import ProjectHelper from '../../../components/SettingsView/ProjectsSettings/ProjectHelper'
import { DV_TAB_CONTACT_PROPERTIES, DV_TAB_ROOT_CONTACTS } from '../../../utils/TabNavigationConstants'
import { updateNotePrivacy, updateNoteTitleWithoutFeed } from '../Notes/notesFirestore'
import {
    updateChatAssistantWithoutFeeds,
    updateChatPrivacy,
    updateChatTitleWithoutFeeds,
} from '../Chats/chatsFirestore'
import { FEED_PUBLIC_FOR_ALL } from '../../../components/Feeds/Utils/FeedsConstants'
import { queueObjectProjectMove, waitForProjectMoveCompletion } from '../projectMoves'
import { createCachedSnapshotGate } from '../cachedSnapshotGate'
import { createFirstSnapshotPerformance } from '../../performance/firestoreSnapshotPerformance'
import {
    mergePendingContacts,
    publishOptimisticContactCreated,
    publishOptimisticContactCreateFailed,
} from './optimisticContactCreate'

const normalizeContactEmail = value =>
    String(value || '')
        .trim()
        .toLowerCase()

const getContactEmails = contact => {
    const emails = [
        ...(Array.isArray(contact?.emails) ? contact.emails : []),
        ...(contact?.email ? [contact.email] : []),
    ]
    return Array.from(new Set(emails.map(normalizeContactEmail).filter(Boolean)))
}

const buildContactEmailFields = (contact, nextEmail, replacePrimary = true) => {
    const normalizedNextEmail = normalizeContactEmail(nextEmail)
    const emails = Array.from(
        new Set([...getContactEmails(contact), ...(normalizedNextEmail ? [normalizedNextEmail] : [])])
    )
    let email = normalizeContactEmail(contact?.email)

    if (!email && normalizedNextEmail) email = normalizedNextEmail
    else if (replacePrimary && normalizedNextEmail) email = normalizedNextEmail

    return { email, emails }
}

const buildContactEmailFieldsFromList = emailList => {
    const emails = Array.from(
        new Set(
            String(emailList || '')
                .split('\n')
                .map(normalizeContactEmail)
                .filter(Boolean)
        )
    )
    return {
        email: emails[0] || '',
        emails,
    }
}

//ACCESS FUNCTIONS

export async function watchProjectContacts(
    projectId,
    callback,
    watcherKey,
    { trackConnectionHealth = false, onError } = {}
) {
    const { loggedUser } = store.getState()
    const allowUserIds = loggedUser.isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUser.uid]
    const snapshotPerformance = createFirstSnapshotPerformance(
        { object_type: 'contacts', scope: 'project', source: 'contacts_board' },
        { sampleRate: 0.02 }
    )
    const gate = createCachedSnapshotGate(() => handleSnapshot, {
        trackConnectionHealth,
        connectionSource: 'contacts_snapshot',
    })

    function handleSnapshot(contactList) {
        const buffered = gate.shouldBuffer(contactList)
        snapshotPerformance.observe(contactList, buffered)
        if (buffered) return

        const contacts = []
        contactList.forEach(contact => {
            contacts.push(mapContactData(contact.id, contact.data()))
        })
        // AT-2508 - a contact the user just added is invisible to this query until the server
        // has written its `readerIds` projection, so it is carried alongside the snapshot until
        // this very snapshot can speak for it. Retiring the pending copy here, in the same
        // delivery that brings the real one, is what stops the row blinking.
        callback(mergePendingContacts(projectId, contacts))
    }

    const unsubscribe = getDb()
        .collection(`/projectsContacts/${projectId}/contacts`)
        .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])
        .onSnapshot({ includeMetadataChanges: true }, handleSnapshot, error => {
            gate.dispose()
            snapshotPerformance.fail()
            console.error('watchProjectContacts: onSnapshot error', { projectId, watcherKey, error })
            onError?.(error)
        })
    globalWatcherUnsub[watcherKey] = gate.wrapUnsubscribe(unsubscribe)
}

export async function watchContactData(projectId, contactId, callback, watcherKey) {
    globalWatcherUnsub[watcherKey] = getDb()
        .doc(`projectsContacts/${projectId}/contacts/${contactId}`)
        .onSnapshot(doc => {
            const contactData = doc.data()
            const contact = contactData ? mapContactData(contactId, contactData) : null
            callback(contact)
        })
}

export async function getContactData(projectId, contactId) {
    const contact = (await getDb().doc(`/projectsContacts/${projectId}/contacts/${contactId}`).get()).data()
    return contact ? mapContactData(contactId, contact) : null
}

export async function getProjectContacts(projectId) {
    const { loggedUser } = store.getState()
    const allowUserIds = loggedUser.isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUser.uid]

    const contactDocs = (
        await getDb()
            .collection(`/projectsContacts/${projectId}/contacts`)
            .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])
            .get()
    ).docs

    const contacts = []
    contactDocs.forEach(doc => {
        const contact = mapContactData(doc.id, doc.data())
        contacts.push(contact)
    })

    return contacts
}

//EDTION AND ADITION FUNCTIONS

export const updateContactEditionData = async (projectId, contactId, editorId) => {
    await getDb().runTransaction(async transaction => {
        const ref = getDb().doc(`projectsContacts/${projectId}/contacts/${contactId}`)
        const doc = await transaction.get(ref)
        if (doc.exists) transaction.update(ref, { lastEditionDate: Date.now(), lastEditorId: editorId })
    })
}

const updateEditionData = data => {
    const { loggedUser } = store.getState()
    data.lastEditionDate = Date.now()
    data.lastEditorId = loggedUser.uid
}

async function updateContactData(projectId, contactId, data, batch) {
    updateEditionData(data)
    const ref = getDb().doc(`projectsContacts/${projectId}/contacts/${contactId}`)
    batch ? batch.update(ref, data) : await ref.update(data)
}

export async function addContactToProject(projectId, contact, onComplete) {
    updateEditionData(contact)

    const contactId = getId()

    const contactToStore = { ...contact, ...buildContactEmailFields(contact, contact.email, false) }
    let feedPhotoUrl = contact.photoURL

    // AT-2508 - put the row on screen NOW, before the avatar upload and the write. Every field
    // is already known locally (the id was minted above), and the list query cannot match this
    // document for several seconds yet - see `optimisticContactCreate.js` for the measurement.
    //
    // The photo fields are deliberately emptied: at this point `photoURL` may still be a Blob or
    // an object URL awaiting `uploadAvatarPhotos`, and this object goes into the shared
    // `projectContacts` slice that the mentions picker and the assistant read too. The pending
    // row draws a spinner in the avatar slot, and the real snapshot brings the real picture.
    publishOptimisticContactCreated(
        projectId,
        contactId,
        mapContactData(contactId, { ...contactToStore, photoURL: '', photoURL50: '', photoURL300: '' })
    )

    try {
        if (contact.photoURL) {
            const pictures = [contact.photoURL, contact.photoURL50, contact.photoURL300]
            const urlList = await uploadAvatarPhotos(
                pictures,
                `projectsContacts/${projectId}/${contactId}/${contactId}@${Date.now()}`,
                `feeds/${projectId}/${contactId}_${getId()}@${Date.now()}`
            )

            contactToStore.photoURL = urlList[0]
            contactToStore.photoURL50 = urlList[1]
            contactToStore.photoURL300 = urlList[2]
            feedPhotoUrl = urlList[3]
        }

        await getDb().doc(`projectsContacts/${projectId}/contacts/${contactId}`).set(contactToStore)
    } catch (error) {
        // Nothing was stored, so the row is a lie - take it away and let the caller report it.
        // Note this only fires on a real rejection: offline the `set()` never settles at all and
        // the contact is genuinely queued, so the row correctly stays.
        publishOptimisticContactCreateFailed(projectId, contactId)
        throw error
    }

    if (onComplete) onComplete({ uid: contactId, ...contactToStore })

    addContactFeedsChain(projectId, contact, feedPhotoUrl, contactId)

    logEvent('new_contact', {
        id: contactId,
        email: contact.email,
    })
}

/**
 * Copy a contact's basic info to a new project.
 * Does NOT copy: contactStatusId, notes, comments, assistantId, etc.
 */
export async function copyContactToProject(targetProjectId, sourceContact, onComplete) {
    const { loggedUser } = store.getState()

    // Fetch full contact from Firestore (Algolia results miss linkedInUrl, email, phone)
    let fullContact = sourceContact
    if (sourceContact.projectId && sourceContact.uid) {
        const fetched = await getContactData(sourceContact.projectId, sourceContact.uid)
        if (fetched) fullContact = fetched
    }

    const newContact = {
        displayName: fullContact.displayName || '',
        photoURL: fullContact.photoURL || '',
        photoURL50: fullContact.photoURL50 || '',
        photoURL300: fullContact.photoURL300 || '',
        company: fullContact.company || '',
        role: fullContact.role || '',
        description: fullContact.description || '',
        extendedDescription: fullContact.extendedDescription || '',
        ...buildContactEmailFields(fullContact, fullContact.email, false),
        phone: fullContact.phone || '',
        linkedInUrl: fullContact.linkedInUrl || '',
        // Default values for new contact in target project
        hasStar: '#FFFFFF',
        isPrivate: false,
        isPublicFor: [FEED_PUBLIC_FOR_ALL, loggedUser.uid],
        recorderUserId: loggedUser.uid,
        lastEditorId: loggedUser.uid,
        lastEditionDate: Date.now(),
        noteId: null,
        isPremium: false,
        lastVisitBoard: {},
        lastVisitBoardInGoals: {},
        assistantId: '',
        commentsData: null,
        openTasksAmount: 0,
        contactStatusId: null,
    }

    const contactId = getId()

    // Write directly - no photo re-upload needed (Firebase Storage URLs are globally accessible)
    await getDb().doc(`projectsContacts/${targetProjectId}/contacts/${contactId}`).set(newContact)

    const copiedContact = { uid: contactId, ...newContact }
    if (onComplete) onComplete(copiedContact)

    addContactFeedsChain(targetProjectId, newContact, newContact.photoURL, contactId)
    logEvent('new_contact', { id: contactId, email: newContact.email })

    return copiedContact
}

export async function setContactProject(currentProject, newProject, contact) {
    const { loggedUser, route } = store.getState()
    const contactId = contact.uid
    await queueObjectProjectMove(currentProject.id, newProject.id, 'contact', contactId)
    const movedContact = await waitForProjectMoveCompletion(currentProject.id, newProject.id, 'contact', contactId)

    if (route === 'ContactDetailedView') {
        NavigationService.navigate('ContactDetailedView', {
            contact: { uid: contactId, ...movedContact },
            project: newProject,
        })

        const projectType = ProjectHelper.getTypeOfProject(loggedUser, newProject.id)
        store.dispatch([
            setSelectedSidebarTab(DV_TAB_ROOT_CONTACTS),
            switchProject(newProject.index),
            setSelectedTypeOfProject(projectType),
            setSelectedNavItem(DV_TAB_CONTACT_PROPERTIES),
        ])
    }

    return movedContact
}

export async function deleteProjectContact(projectId, contact, contactId) {
    const batch = new BatchWrapper(getDb())
    await createContactDeletedFeed(projectId, contact, contactId, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.delete(getDb().doc(`projectsContacts/${projectId}/contacts/${contactId}`))
    batch.commit()
}

export const setContactAssistant = async (projectId, contactId, assistantId, needGenerateUpdate) => {
    const batch = new BatchWrapper(getDb())
    updateContactData(projectId, contactId, { assistantId }, batch)
    await updateChatAssistantWithoutFeeds(projectId, contactId, assistantId, batch)
    await batch.commit()
    if (needGenerateUpdate) await createContactAssistantChangedFeed(projectId, assistantId, contactId, null, null)
}

export const updateContactNote = async (projectId, contactId, noteId) => {
    await updateContactData(projectId, contactId, { noteId }, null)
}

export const updateContactLastCommentData = async (projectId, contactId, lastComment, lastCommentType) => {
    getDb()
        .doc(`projectsContacts/${projectId}/contacts/${contactId}`)
        .update({
            [`commentsData.lastComment`]: lastComment,
            [`commentsData.lastCommentType`]: lastCommentType,
            [`commentsData.amount`]: firebase.firestore.FieldValue.increment(1),
        })
}

export const resetContactLastCommentData = async (projectId, contactId) => {
    const ref = getDb().doc(`projectsContacts/${projectId}/contacts/${contactId}`)
    const doc = await ref.get()
    if (doc.exists) {
        const data = doc.data()
        if (data.commentsData && data.commentsData.amount > 0) {
            ref.update({
                [`commentsData.lastComment`]: null,
                [`commentsData.lastCommentType`]: null,
                [`commentsData.amount`]: 0,
            })
        }
    }
}

export async function setProjectContactName(projectId, contact, contactId, newName, oldName) {
    let batch = new BatchWrapper(getDb())
    updateContactData(projectId, contactId, { displayName: newName }, batch)
    if (contact.noteId) await updateNoteTitleWithoutFeed(projectId, contact.noteId, newName, batch)
    await updateChatTitleWithoutFeeds(projectId, contact.uid, newName, batch)
    batch.commit()

    batch = new BatchWrapper(getDb())
    await createContactNameChangedFeed(projectId, contact, contactId, newName, oldName, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactCompany(projectId, contact, contactId, newCompany, oldCompany) {
    await updateContactData(projectId, contactId, { company: newCompany }, null)

    const batch = new BatchWrapper(getDb())
    await createContactCompanyChangedFeed(projectId, contact, contactId, newCompany, oldCompany, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactRole(projectId, contact, contactId, newRole, oldRole) {
    await updateContactData(projectId, contactId, { role: newRole }, null)

    const batch = new BatchWrapper(getDb())
    await createContactRoleChangedFeed(projectId, contact, contactId, newRole, oldRole, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactDescription(projectId, contact, contactId, newDescription, oldDescription) {
    await updateContactData(
        projectId,
        contactId,
        { description: TasksHelper.getTaskNameWithoutMeta(newDescription), extendedDescription: newDescription },
        null
    )

    const batch = new BatchWrapper(getDb())
    await createContactDescriptionChangedFeed(projectId, contact, contactId, newDescription, oldDescription, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactPicture(projectId, contact, contactId, pictureFile) {
    return runWithLoading('update_contact_picture', async () => {
        if (!pictureFile) {
            await deleteFolderFilesInStorage(`projectsContacts/${projectId}/${contactId}`)
            await updateContactData(projectId, contactId, { photoURL: '', photoURL50: '', photoURL300: '' }, null)

            return
        }

        const pictures = await proccessPictureForAvatar(pictureFile)

        if (pictures.length > 0) {
            await deleteFolderFilesInStorage(`projectsContacts/${projectId}/${contactId}`)
            const urlList = await uploadAvatarPhotos(
                pictures,
                `projectsContacts/${projectId}/${contactId}/${contactId}@${Date.now()}`,
                `feeds/${projectId}/${contactId}_${getId()}@${Date.now()}`
            )

            const contactToStore = {
                ...contact,
                photoURL: urlList[0],
                photoURL50: urlList[1],
                photoURL300: urlList[2],
            }

            await updateContactData(projectId, contactId, contactToStore, null)

            updateContactPhotoFeedsChain(projectId, contact, contactId, urlList)
        }
    })
}

export async function setProjectContactPhone(projectId, contact, contactId, newPhoneNumber, oldPhoneNumber) {
    await updateContactData(projectId, contactId, { phone: newPhoneNumber }, null)

    const batch = new BatchWrapper(getDb())
    await createContactPhoneNumberChangedFeed(projectId, contact, contactId, newPhoneNumber, oldPhoneNumber, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactEmail(projectId, contact, contactId, newEmail, oldEmail) {
    await updateContactData(projectId, contactId, buildContactEmailFields(contact, newEmail, true), null)

    const batch = new BatchWrapper(getDb())
    await createContactEmailChangedFeed(projectId, contact, contactId, newEmail, oldEmail, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactEmails(projectId, contact, contactId, emailList, oldEmail) {
    const emailFields = buildContactEmailFieldsFromList(emailList)
    await updateContactData(projectId, contactId, emailFields, null)

    const batch = new BatchWrapper(getDb())
    if (emailFields.email !== oldEmail) {
        await createContactEmailChangedFeed(projectId, contact, contactId, emailFields.email, oldEmail, batch)
    }
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactPrivacy(projectId, contact, contactId, privacy, isPublicFor) {
    updateContactData(projectId, contactId, { isPrivate: privacy, isPublicFor: isPublicFor }, null)

    if (contact.noteId) {
        const followersIds = await getObjectFollowersIds(projectId, 'contacts', contact.uid)
        updateNotePrivacy(projectId, contact.noteId, privacy, isPublicFor, followersIds, false, null)
    }
    updateChatPrivacy(projectId, contactId, 'contacts', isPublicFor)

    const batch = new BatchWrapper(getDb())
    await createContactPrivacyChangedFeed(projectId, contact, contactId, privacy, isPublicFor, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactHighlight(projectId, contact, contactId, highlightColor) {
    updateContactData(projectId, contactId, { hasStar: highlightColor }, null)

    const batch = new BatchWrapper(getDb())
    await createContactHighlightChangedFeed(projectId, contact, contactId, highlightColor, batch)
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export function setContactLastVisitedBoardDate(projectId, contactId, lastVisitBoardProperty) {
    const { loggedUser } = store.getState()
    // Update directly without triggering lastEditionDate change
    const ref = getDb().doc(`projectsContacts/${projectId}/contacts/${contactId}`)
    ref.update({ [`${lastVisitBoardProperty}.${projectId}.${loggedUser.uid}`]: Date.now() })
}

export async function setProjectContactStatus(projectId, contact, contactId, newStatusId) {
    await updateContactData(projectId, contactId, { contactStatusId: newStatusId }, null)

    const batch = new BatchWrapper(getDb())
    const followContactData = {
        followObjectsType: FOLLOWER_CONTACTS_TYPE,
        followObjectId: contactId,
        followObject: contact,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followContactData, batch)
    batch.commit()
}

export async function setProjectContactLinkedInUrl(projectId, contact, contactId, newUrl) {
    await updateContactData(projectId, contactId, { linkedInUrl: newUrl }, null)
}

/**
 * "Enrich profile": starts an assistant research run in the contact's chat. Resolves as soon as
 * the run has posted its answer (or asked its question) — callers should not block the UI on it.
 * A client-generated requestId keys the server's run lock, so a request the browser replays after
 * a sleep/wake lands on the same comment and the same run.
 */
export async function enrichContactProfile(projectId, contactId, assistantId) {
    const requestId = getId()
    const result = await callEnrichContactProfile({
        projectId,
        contactId,
        assistantId: assistantId || '',
        requestId,
    })
    if (result?.error === 'insufficient_gold') {
        return { success: false, error: 'insufficient_gold', requestId }
    }
    return { ...(result || {}), requestId }
}
